import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, Volume2, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Single nav shell for Operator and Supervisor/Manager — previously two
// separately hand-written files (OperatorLayout.jsx, SupervisorLayout.jsx)
// with identical structure (logo, nav list, active-state highlight, user
// footer, mobile drawer) and no code shared between them, free to drift.
// Admin's Sidebar/Layout/Header are NOT migrated here yet — that shell is
// visually a different generation of the UI (different active-state
// treatment, different logo block) and unifying it is a deliberate later
// step (Phase 3), not something that should happen as a side effect of
// this refactor.
//
// navItems: [{ to, icon, label, badge? }] — badge is a ready-to-render node
// (a count pill, a pulsing dot, etc.), computed by the page/route that owns
// that data; AppShell doesn't know or care what it means.
//
// contentClassName: extra classes on the <main> wrapping Outlet. Operator
// and Supervisor pages each own their own inner padding (some want
// full-bleed sections, sticky sub-headers, etc.), so this defaults to ''.
// Admin's older pages were never built that way — they assumed the page
// shell itself provided the p-6 gutter — so AdminLayout passes it here
// instead of retrofitting all 16 pages individually.
export default function AppShell({ logoLabel = 'Gate PA System', roleLabel, navItems, mobileHeaderExtra, contentClassName = '' }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 h-16 px-5 bg-blue-900 flex-shrink-0">
        <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
          <Volume2 size={18} className="text-white" />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-tight">{logoLabel}</p>
          <p className="text-blue-300 text-xs">{roleLabel}</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">
          Navigation
        </p>
        {navItems.map(({ to, icon: Icon, label, badge }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors relative ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={18} className={isActive ? 'text-blue-600' : 'text-gray-400'} />
                <span className="flex-1">{label}</span>
                {badge}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r-full" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="flex-shrink-0 border-t border-gray-100 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-blue-700">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* ── Desktop sidebar ─────────────────────────────────── */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200 shadow-sm z-30">
        <SidebarContent />
      </aside>

      {/* ── Mobile overlay ──────────────────────────────────── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-2xl flex flex-col">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* ── Main content ────────────────────────────────────── */}
      {/* min-w-0: a flex item defaults to min-width:auto, so one wide child
          (a tab strip, a table) would otherwise widen the whole column past
          the viewport on phones instead of wrapping/scrolling inside it. */}
      <div className="flex-1 min-w-0 lg:pl-60 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 text-gray-500 hover:bg-gray-100 rounded-lg"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <Volume2 size={18} className="text-blue-700 flex-shrink-0" />
            <span className="font-bold text-gray-900 text-sm truncate">{logoLabel}</span>
          </div>
          {mobileHeaderExtra && <div className="ml-auto flex flex-wrap justify-end items-center gap-1.5 flex-shrink-0 whitespace-nowrap">{mobileHeaderExtra}</div>}
        </header>

        {/* Page content */}
        <main className={`flex-1 overflow-y-auto ${contentClassName}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
